// src/pages/BookingSuccess.tsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import {
  CheckCircle,
  Loader,
  AlertTriangle,
  Calendar,
  MapPin,
  ArrowRight,
  Home,
  ShieldCheck,
  Mail,
  LogIn,
  ExternalLink,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ForfeoCheckoutNetworkPanel from '../components/network/ForfeoCheckoutNetworkPanel';
import confetti from 'canvas-confetti';

// On utilise Supabase Edge Functions + Resend
type Status = 'loading' | 'success' | 'error';

const addDays = (d: Date, days: number) => {
  const x = new Date(d);
  x.setDate(x.getDate() + days);
  return x;
};

const safeParseNumber = (v: string | null, fallback: number) => {
  if (!v) return fallback;
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

const BookingSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // États pour gérer la logique et l'UI
  const [status, setStatus] = useState<Status>('loading');
  const [message, setMessage] = useState('Finalisation de votre réservation...');

  // Identité du partenaire
  const [business, setBusiness] = useState<any>(null);
  const [businessLoading, setBusinessLoading] = useState(false);

  // Si l’utilisateur n’est pas loggé au retour de Stripe
  const [needsLogin, setNeedsLogin] = useState(false);

  // Pour éviter un double run en dev/strict mode
  const didRunRef = useRef(false);

  // --- Params robustes ---
  const sessionId =
    searchParams.get('session_id') ||
    searchParams.get('sessionId') ||
    searchParams.get('checkout_session_id') ||
    null;

  const paymentIntent =
    searchParams.get('payment_intent') ||
    searchParams.get('paymentIntent') ||
    searchParams.get('pi') ||
    null;

  // "Référence paiement" : on préfère payment_intent si fourni, sinon session_id
  const paymentRef = paymentIntent || sessionId;

  const businessId =
    searchParams.get('business_id') ||
    searchParams.get('businessId') ||
    searchParams.get('b') ||
    null;

  const dateStr =
    searchParams.get('date') ||
    searchParams.get('check_in') ||
    searchParams.get('checkIn') ||
    searchParams.get('check_in_date') ||
    null;

  const endDateStr =
    searchParams.get('check_out') ||
    searchParams.get('checkOut') ||
    searchParams.get('check_out_date') ||
    null;

  const partySize = safeParseNumber(searchParams.get('party_size') || searchParams.get('partySize'), 2);

  // total_price : si tu passes un amount en dollars dans l’URL, on le prend
  // sinon fallback à 249 (comme ton code actuel)
  const totalPrice = safeParseNumber(searchParams.get('total') || searchParams.get('total_price') || searchParams.get('amount'), 249);

  // Date du jour pour l'affichage
  const displayDate = dateStr ? new Date(dateStr).toLocaleDateString('fr-FR') : new Date().toLocaleDateString('fr-FR');

  const displayBusiness = useMemo(() => {
    const name = business?.name || 'Hébergement Partenaire Forfeo';
    const region = business?.region || business?.city || business?.ville || '';
    const address = business?.address || business?.adresse || business?.location || '';
    const phone = business?.phone || business?.telephone || business?.tel || '';
    return { name, region, address, phone };
  }, [business]);

  useEffect(() => {
    let mounted = true;

    const triggerConfetti = () => {
      const duration = 2500;
      const end = Date.now() + duration;
      (function frame() {
        confetti({ particleCount: 3, angle: 60, spread: 55, origin: { x: 0 }, colors: ['#2563eb', '#ec4899', '#f59e0b'] });
        confetti({ particleCount: 3, angle: 120, spread: 55, origin: { x: 1 }, colors: ['#2563eb', '#ec4899', '#f59e0b'] });
        if (Date.now() < end) requestAnimationFrame(frame);
      })();
    };

    const fetchBusiness = async () => {
      if (!businessId) return;
      try {
        setBusinessLoading(true);
        const { data, error } = await supabase
          .from('businesses')
          .select('*')
          .eq('id', businessId)
          .maybeSingle();

        if (!mounted) return;
        if (error || !data) return;
        setBusiness(data);
      } catch (e) {
        console.error('Erreur fetch business:', e);
      } finally {
        if (mounted) setBusinessLoading(false);
      }
    };

    const setSuccess = (msg: string) => {
      setStatus('success');
      setMessage(msg);
      triggerConfetti();
      fetchBusiness();
    };

    const saveBooking = async () => {
      // Évite double exécution (React strict/dev)
      if (didRunRef.current) return;
      didRunRef.current = true;

      // Vérifs minimales
      if (!paymentRef || !businessId) {
        setStatus('error');
        setMessage("Informations de paiement manquantes (session/payment/business).");
        return;
      }

      try {
        // 1) User : si pas loggé, on ne bloque pas l’écran.
        const { data: userRes } = await supabase.auth.getUser();
        const user = userRes?.user;

        if (!user) {
          setNeedsLogin(true);
          setSuccess("Paiement validé ✅ Connectez-vous pour enregistrer et retrouver votre réservation dans le Club.");
          return;
        }

        // 2) Idempotence : on évite les doublons
        const { data: existing, error: existingErr } = await supabase
          .from('reservations')
          .select('id')
          .eq('payment_intent_id', paymentRef)
          .maybeSingle();

        if (existingErr) {
          // On log mais on continue (selon RLS/colonnes)
          console.warn('Idempotence check error:', existingErr);
        }

        if (existing?.id) {
          // On tente quand même l'email (non bloquant)
          try {
            await supabase.functions.invoke('send-reservation-confirmation', {
              body: { reservation_id: existing.id },
            });
          } catch (e) {
            console.warn("Email (existing) non envoyé:", e);
          }

          setSuccess('Votre réservation est déjà confirmée !');
          return;
        }

        // 3) Préparer dates slot_start / slot_end
        const checkIn = dateStr ? new Date(dateStr) : new Date();
        const checkOut = endDateStr ? new Date(endDateStr) : addDays(checkIn, 1);

        // 4) Insert dans 'reservations' (Option A)
        // Payload "large" puis fallback si colonnes absentes (pour ne pas casser ton schéma actuel)
        const basePayload: any = {
          user_id: user.id,
          business_id: businessId,
          customer_name: user.user_metadata?.full_name || user.email?.split('@')[0] || 'Voyageur',
          customer_email: user.email,
          slot_start: checkIn.toISOString(),
          slot_end: checkOut.toISOString(),
          status: 'confirmed',
          payment_intent_id: paymentRef,
          total_price: totalPrice,
          party_size: partySize,
        };

        const tryInsert = async (payload: any) => {
          const { data, error } = await supabase
            .from('reservations')
            .insert(payload)
            .select()
            .single();
          return { data, error };
        };

        let insertRes = await tryInsert(basePayload);

        // Fallback : si certaines colonnes n'existent pas (ou RLS), on réduit
        if (insertRes.error) {
          console.warn("Insert reservations error (full payload):", insertRes.error);

          const minimalPayload: any = {
            user_id: basePayload.user_id,
            business_id: basePayload.business_id,
            customer_email: basePayload.customer_email,
            slot_start: basePayload.slot_start,
            slot_end: basePayload.slot_end,
            status: basePayload.status,
            payment_intent_id: basePayload.payment_intent_id,
          };

          insertRes = await tryInsert(minimalPayload);
        }

        if (insertRes.error) {
          console.error('Erreur insert reservations:', insertRes.error);
          setStatus('error');
          setMessage("Paiement OK, mais impossible d'enregistrer la réservation (RLS/table).");
          return;
        }

        const newRes = insertRes.data;
        console.log("✅ Réservation enregistrée dans 'reservations', ID:", newRes?.id);

        // 5) Appel Edge Function (Resend) — non bloquant
        try {
          const { error: funcError } = await supabase.functions.invoke('send-reservation-confirmation', {
            body: { reservation_id: newRes.id },
          });

          if (funcError) {
            console.warn("❌ Email non envoyé (Edge Function):", funcError);
          } else {
            console.log("✉️ Email envoyé via Resend");
          }
        } catch (e) {
          console.warn("❌ Email invoke exception:", e);
        }

        setSuccess('Paiement validé et confirmation envoyée par courriel !');
      } catch (error: any) {
        console.error('Erreur critique:', error);
        setStatus('error');
        setMessage('Erreur : ' + (error?.message || 'Inconnue'));
      }
    };

    saveBooking();

    return () => {
      mounted = false;
    };
  }, [paymentRef, businessId, dateStr, endDateStr, partySize, totalPrice]);

  return (
    <div className="min-h-screen bg-slate-50 font-sans flex flex-col">
      <Header />

      <div className="flex-grow flex items-center justify-center p-4 pt-24 pb-12">
        <div className="bg-white max-w-lg w-full rounded-3xl shadow-2xl border border-slate-100 overflow-hidden relative">

          {status === 'loading' && (
            <div className="p-12 text-center">
              <Loader className="animate-spin h-12 w-12 text-blue-600 mx-auto mb-6" />
              <h2 className="text-xl font-black text-slate-900 mb-2">Confirmation en cours...</h2>
              <p className="text-slate-500 text-sm">{message}</p>
            </div>
          )}

          {status === 'error' && (
            <div className="p-12 text-center animate-in shake">
              <div className="h-20 w-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <AlertTriangle className="h-10 w-10 text-red-600" />
              </div>
              <h2 className="text-2xl font-black text-slate-900 mb-2">Oups...</h2>
              <p className="text-red-500 font-bold mb-4">{message}</p>

              <div className="space-y-3">
                <button
                  onClick={() => navigate('/')}
                  className="w-full bg-slate-900 text-white py-3 rounded-xl font-bold hover:bg-slate-800 transition"
                >
                  Retour à l'accueil
                </button>

                {businessId && (
                  <button
                    onClick={() => navigate(`/place/${businessId}`)}
                    className="w-full bg-white text-slate-700 py-3 rounded-xl font-bold border border-slate-200 hover:bg-slate-50 transition flex items-center justify-center gap-2"
                  >
                    Voir le partenaire <ExternalLink size={16} />
                  </button>
                )}
              </div>
            </div>
          )}

          {status === 'success' && (
            <div className="animate-in slide-in-from-bottom-4 duration-700">
              <div className="h-32 bg-gradient-to-r from-blue-600 to-indigo-600 relative">
                <div className="absolute -bottom-10 left-1/2 -translate-x-1/2 w-20 h-20 bg-white rounded-full flex items-center justify-center p-2 shadow-lg">
                  <div className="w-full h-full bg-green-500 rounded-full flex items-center justify-center text-white animate-in zoom-in duration-500">
                    <CheckCircle size={40} strokeWidth={3} />
                  </div>
                </div>
              </div>

              <div className="pt-14 pb-8 px-8 text-center">
                <h1 className="text-3xl font-black text-slate-900 mb-2">C'est réservé !</h1>
                <p className="text-slate-500 text-sm mb-6">{message}</p>

                <div className="flex flex-col items-center gap-2 mb-8">
                  {!needsLogin ? (
                    <div className="bg-green-50 text-green-800 text-xs font-bold px-4 py-2 rounded-full inline-flex items-center gap-2 border border-green-100">
                      <Mail size={14} /> Confirmation envoyée par courriel
                    </div>
                  ) : (
                    <div className="bg-amber-50 text-amber-800 text-xs font-bold px-4 py-2 rounded-full inline-flex items-center gap-2 border border-amber-100">
                      <AlertTriangle size={14} /> Connectez-vous pour enregistrer la réservation
                    </div>
                  )}

                  <div className="bg-slate-50 text-slate-700 text-xs font-bold px-4 py-2 rounded-full inline-flex items-center gap-2 border border-slate-100">
                    <ShieldCheck size={14} /> Séjour vérifié possible via votre réservation (reservations)
                  </div>
                </div>

                <div className="mb-6 text-left">
                  <ForfeoCheckoutNetworkPanel variant="success" compact />
                </div>

                <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100 text-left mb-8 space-y-4 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="bg-white p-2 rounded-lg text-blue-600 shadow-sm border border-slate-50">
                      <Calendar size={20} />
                    </div>
                    <div>
                      <p className="text-[10px] font-black uppercase text-slate-400">ARRIVÉE</p>
                      <p className="font-bold text-slate-900">{displayDate}</p>
                    </div>
                  </div>

                  <div className="w-full h-[1px] bg-slate-200"></div>

                  <div className="flex items-start gap-4">
                    <div className="bg-white p-2 rounded-lg text-orange-500 shadow-sm border border-slate-50">
                      <MapPin size={20} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[10px] font-black uppercase text-slate-400">DESTINATION</p>
                      <p className="font-bold text-slate-900 truncate">
                        {businessLoading ? 'Chargement...' : displayBusiness.name}
                      </p>
                      {displayBusiness.region && <p className="text-xs text-slate-500">{displayBusiness.region}</p>}
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  {needsLogin ? (
                    <button
                      onClick={() => navigate('/club')}
                      className="w-full bg-slate-900 text-white py-4 rounded-xl font-bold hover:bg-indigo-700 transition shadow-lg flex items-center justify-center gap-2 group"
                    >
                      Se connecter <LogIn size={18} />
                    </button>
                  ) : (
                    <button
                      onClick={() => navigate('/club?tab=trips')}
                      className="w-full bg-slate-900 text-white py-4 rounded-xl font-bold hover:bg-indigo-700 transition shadow-lg flex items-center justify-center gap-2 group"
                    >
                      Voir mon billet dans le Club <ArrowRight size={18} className="group-hover:translate-x-1 transition" />
                    </button>
                  )}

                  {businessId && (
                    <button
                      onClick={() => navigate(`/place/${businessId}`)}
                      className="w-full bg-white text-slate-700 py-3 rounded-xl font-bold border border-slate-200 hover:bg-slate-50 transition flex items-center justify-center gap-2"
                    >
                      Voir le partenaire <ExternalLink size={16} />
                    </button>
                  )}

                  <button
                    onClick={() => navigate('/')}
                    className="w-full bg-white text-slate-600 py-3 rounded-xl font-bold border border-slate-200 hover:bg-slate-50 transition flex items-center justify-center gap-2"
                  >
                    <Home size={16} /> Retour à l'accueil
                  </button>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>

      <Footer />
    </div>
  );
};

export default BookingSuccess;
